import {useMemo} from "react";
import {Area, Bar, YAxis} from "recharts";
import {BaseChart, ChartComponent} from "./BaseChart";
import {TimeSeriesDataProps} from "../../../types/timeSeriesDataProps";
import {FieldResult} from "../../../types/domain/fieldResult";
import {useMeasureSystemUtils} from "../../../hooks/measureSystemUtilsHooks";
import {MeasureSystemUtils} from "../../../types/domain/measureSystemUtils";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";

export interface PrecipitationChartDataProps {
    precipitation: number;
}

export const PrecipitationChart: ChartComponent = ({data}) => {
    const {t} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const chartData = useMemo(() => data.map((d) => mapFieldResultsToChartData(d, msUtils)), [data, msUtils]);

    return (
        <BaseChart
            title={t("insights.charts.precipitation.title")}
            yLabel={`${t("insights.charts.precipitation.y_label")}, ${msUtils.getRainfallUnits()}`}
            data={chartData}
            helpPopoverTranslationKey="help.popover.fields.precipitation"
        >
            <Area
                type="monotone"
                dataKey="windSpeed"
                name={t("insights.charts.precipitation.area_name")}
                unit={msUtils.getWindSpeedUnits()}
                yAxisId="left"
                stroke="#00CA4D"
                fill="#A3FEC6"
                strokeWidth={2}
                isAnimationActive={false}
                fillOpacity="1"
                opacity="1"
            />
            <Bar
                type="monotone"
                dataKey="precipitation"
                name={t("insights.charts.precipitation.bar_name")}
                unit={msUtils.getRainfallUnits()}
                yAxisId="right"
                fill="#5161C1"
                isAnimationActive={false}
            />
            <YAxis
                yAxisId="left"
                domain={["auto", "auto"]}
                fontSize={14}
                label={{
                    value: `${t("insights.charts.precipitation.y_axis")}, ${msUtils.getWindSpeedUnits()}`,
                    angle: -90,
                    dx: -20,
                    dy: 0,
                    textAnchor: "middle",
                }}
                orientation="left"
            />
            <YAxis
                yAxisId="right"
                domain={["auto", "auto"]}
                fontSize={14}
                label={{
                    value: `${t("insights.charts.precipitation.y_label")}, ${msUtils.getRainfallUnits()}`,
                    angle: 90,
                    dx: 20,
                    dy: 0,
                    textAnchor: "middle",
                }}
                orientation="right"
            />
        </BaseChart>
    );
};

const mapFieldResultsToChartData = (r: FieldResult, msUtils: MeasureSystemUtils): DataProps => ({
    date: dayjs(r.date).toDate(),
    precipitation: [0, msUtils.getRainfall(r.aggregatedProps.precipitation)],
    windSpeed: msUtils.getWindSpeed(
        /**
         * Historical data is not available for wind speed for today (wind_speed_24), so we use the forecasted wind speed(wind_speed_24_T1)
         * because we do not want to show an empty graph to the user
         */
        r.aggregatedProps.wind_speed_24 ?? r.aggregatedProps.wind_speed_24_T1,
    ),
});

export interface DataProps extends TimeSeriesDataProps {
    precipitation: number[];
    windSpeed: number;
}
